import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePasswordlessSignIn } from 'services/repositories/user/user';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { defaultData } from 'pages/Auth/contexts/LoginStateContext/LoginStateContext';
import { useLoginForm } from 'pages/Auth/contexts/LoginStateContext/useLoginForm';
import { useLoginState } from 'pages/Auth/contexts/LoginStateContext/useLoginState';
import { Button } from 'components/Form/Button';
import { Input } from 'components/Form/Input';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';

import { Credentials } from '../../types';

export const PasswordlessLoginForm = ({
  passwordlessOnly,
  previousUrl,
}: {
  passwordlessOnly?: boolean;
  previousUrl?: string;
}) => {
  const { t } = useTranslation();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);
  const { mutate: passwordlessSignIn, isLoading, isSuccess, reset: resetMutation } = usePasswordlessSignIn();
  const { switchToPasswordLogin, switchToPasswordlessSuccess } = useLoginState();
  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useLoginForm();

  const onPasswordlessSignIn = async (credentials: Omit<Credentials, 'password'>) => {
    setNonInputErrors([]);

    const fullCredentials = previousUrl ? { ...credentials, previous_url: previousUrl } : credentials;

    passwordlessSignIn(fullCredentials, {
      onError: (err) => {
        processSubmissionErrors({ error: err, setInputError: setError, setNonInputErrors, defaultData, t });
        throw err;
      },
    });
  };

  useLayoutEffect(() => {
    if (isSuccess) {
      resetMutation(); // a way to reset isSuccess flag
      switchToPasswordlessSuccess();
    }
  }, [isSuccess, switchToPasswordlessSuccess, resetMutation]);

  return (
    <form onSubmit={handleSubmit(onPasswordlessSignIn)} className="space-y-6">
      <ValidationErrors errors={nonInputErrors} />
      <Input
        {...register('email', { required: true })}
        required
        placeholder={t('auth:email')}
        autoComplete="username"
        label={t('auth:emailAddress')}
        type="email"
        errors={errors.email}
        readOnly={isLoading}
        wrapperClassName="mb-0!"
      />

      <Button
        type="submit"
        fullWidth
        size="extra-large"
        className="mt-4! mb-0!"
        loading={isLoading}
        analyticsId="login:login-passwordless"
      >
        {t('auth:signInPasswordless')}
      </Button>
      {!passwordlessOnly && (
        <div className="flex w-full flex-col gap-6">
          <Button
            type="button"
            variant="text"
            className="text-gray-900! underline"
            fullWidth
            analyticsId="login:toggle-login-method"
            onClick={switchToPasswordLogin}
          >
            {t('auth:change-method.loginWithPassword')}
          </Button>
        </div>
      )}
    </form>
  );
};
